import React, { useState, useEffect } from 'react';
import { Settings, Mail, Smartphone, Save, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    phone_number: '',
    notification_types: {
      swipe_accepted: true,
      tip_accepted: true,
      tip_rejected: true,
      tip_countered: true,
      match_created: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiService.notifications.getSettings();
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      toast({
        title: "Error",
        description: "Failed to load notification settings.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Validate phone number if SMS is enabled
      if (settings.sms_notifications && !settings.phone_number.trim()) {
        toast({
          title: "Phone Number Required",
          description: "Please enter a phone number to enable SMS notifications.",
          variant: "destructive"
        });
        return;
      }

      const response = await apiService.notifications.updateSettings(settings);
      
      if (response.data.success) {
        toast({
          title: "Settings Saved! ✅",
          description: "Your notification preferences have been updated."
        });
        onClose();
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast({
        title: "Error",
        description: "Failed to save notification settings.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const updateNotificationType = (type, enabled) => {
    setSettings(prev => ({
      ...prev,
      notification_types: {
        ...prev.notification_types,
        [type]: enabled
      }
    }));
  };

  const notificationTypeLabels = {
    swipe_accepted: {
      title: "Match Notifications",
      description: "When someone likes you back and creates a match"
    },
    tip_accepted: {
      title: "Tip Accepted",
      description: "When someone accepts your tip offer"
    },
    tip_rejected: {
      title: "Tip Declined",
      description: "When someone declines your tip offer"
    },
    tip_countered: {
      title: "Tip Countered",
      description: "When someone counters your tip offer with a different amount"
    },
    match_created: {
      title: "New Matches",
      description: "When you get a new match"
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-gray-600" />
            <span>Notification Settings</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Delivery Methods */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Delivery Methods</CardTitle>
                <CardDescription>
                  Choose how you want to receive notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Email Notifications */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-4 h-4 text-blue-500" />
                    <div>
                      <Label className="text-sm font-medium">Email Notifications</Label>
                      <p className="text-xs text-gray-500">Receive notifications via email</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.email_notifications}
                    onCheckedChange={(checked) => updateSettings('email_notifications', checked)}
                  />
                </div>

                {/* SMS Notifications */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Smartphone className="w-4 h-4 text-green-500" />
                      <div>
                        <Label className="text-sm font-medium">SMS Notifications</Label>
                        <p className="text-xs text-gray-500">Receive notifications via text message</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.sms_notifications}
                      onCheckedChange={(checked) => updateSettings('sms_notifications', checked)}
                    />
                  </div>

                  {settings.sms_notifications && (
                    <div className="ml-7">
                      <Label htmlFor="phone" className="text-xs text-gray-600">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={settings.phone_number}
                        onChange={(e) => updateSettings('phone_number', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notification Types */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Notification Types</CardTitle>
                <CardDescription>
                  Choose which events you want to be notified about
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(notificationTypeLabels).map(([type, { title, description }]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex-1">
                        <Label className="text-sm font-medium">{title}</Label>
                        <p className="text-xs text-gray-500">{description}</p>
                      </div>
                      <Switch
                        checked={settings.notification_types[type]}
                        onCheckedChange={(checked) => updateNotificationType(type, checked)}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Privacy Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Privacy:</strong> Your notification preferences are private and can be changed at any time. 
                We'll only send notifications for the events you've enabled above.
              </p>
            </div>

            <Separator />

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationSettings;